import React, { ReactNode } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { useUpdateUserDetailsMutation } from 'services';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import useUserStore from 'store/user';
import SearchCompanyList from 'components/Common/SearchCompanyList';
import { getDateString, getMinDate, validateString } from 'utils';
import { experienceInitialValues, experienceSchema } from 'constants/schemaValidations';
import useTranslate from 'hooks/useTranslate';
import { ExperienceInterface } from 'constants/interfaces';
import useCommonStore from 'store/common';
import moment from 'moment';

const AddExperience: React.FC<any> = () => {
	const { translate } = useTranslate();
	const { profileData }: any = useUserStore((state) => state);
	const { mutateAsync: updateProfile } = useUpdateUserDetailsMutation();
	const { experience: savedData = [] } = profileData;

	const { modalConfig = {} }: any = useCommonStore((state) => state);

	const getLimitedFields = (data: any[]): ExperienceInterface[] => {
		return data?.map((item) => ({
			employer: item.employer || '',
			jobTitle: item.jobTitle || '',
			employerId: item.employerId || null,
			startDate: item.startDate || '',
			endDate: item.endDate || '',
			rolesAndResponsibility: item.rolesAndResponsibility || '',
			currentlyWorking: item.currentlyWorking === true || false,
		}));
	};

	const formik: any = useFormik({
		initialValues: {
			...experienceInitialValues,
			...(Number.isInteger(modalConfig?.id) && savedData[modalConfig.id]
				? {
						...savedData[modalConfig.id],
						startDate: savedData[modalConfig.id]?.startDate
							? moment(savedData[modalConfig.id].startDate, 'MMMM YYYY').toDate()
							: '',
						endDate: savedData[modalConfig.id]?.endDate
							? moment(savedData[modalConfig.id].endDate, 'MMMM YYYY').toDate()
							: '',
					}
				: {}),
		},
		validationSchema: experienceSchema(translate),
		enableReinitialize: true,
		onSubmit: (values: any) => {
			const limitedFieldsData: ExperienceInterface[] =
				savedData?.length > 0 ? getLimitedFields(savedData) : [];

			const newDatas = [...limitedFieldsData];

			if (Number.isInteger(modalConfig?.id)) {
				newDatas[modalConfig.id] = {
					...values,
					startDate: getDateString(formik.values?.startDate),
					endDate: values?.currentlyWorking ? '' : getDateString(formik.values?.endDate),
				};
			} else {
				newDatas.push({
					...values,
					startDate: getDateString(formik.values?.startDate),
					endDate: values?.currentlyWorking ? '' : getDateString(formik.values?.endDate),
				});
			}

			updateProfile({ experience: newDatas, saveType: 'addExperience' })
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
					}
				})
				.finally(() => modalConfig?.onClose());
		},
	});

	const renderError = <T extends keyof ExperienceInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => modalConfig?.onClose()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Profile.addExprience')}</h4>
					<Row>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('Profile.addExprienceJobTitle')}
								</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterJobTitle')}
									className="rounded-pill"
									name="jobTitle"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.jobTitle}
									autoComplete="off"
								/>
								{renderError('jobTitle')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('Profile.experienceCompanyName')}
								</Form.Label>
								<SearchCompanyList
									selectProps={{
										placeholder: translate('PlaceHodler.enterCompanyName'),
										onChange: (value: any) => {
											formik.setFieldValue('employerId', value.id);
											formik.setFieldValue('employer', value.companyName);
										},
										onCreateOption: (value: any) => {
											if (value.length > 2 && validateString(value)) {
												formik.setFieldValue('employer', value);
											}
										},
										name: 'employer',
									}}
									fieldValue={formik.values.employer}
									onBlur={formik.handleBlur}
									formik={formik}
								/>
								{renderError('employer') || renderError('employerId')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('Common.duration')} ({translate('Common.from')})
								</Form.Label>
								<DatePicker
									selected={formik.values.startDate}
									onChange={(date: any) => formik.setFieldValue('startDate', date)}
									name="startDate"
									onBlur={formik.handleBlur}
									dateFormat="MM/yyyy"
									showMonthYearPicker
									placeholderText={`${translate('PlaceHolder.date')}`}
									maxDate={new Date()}
									minDate={getMinDate()}
									autoComplete="off"
								/>
								{renderError('startDate')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('Common.duration')} ({translate('Common.to')})
								</Form.Label>
								<Form.Check
									type="switch"
									// id="custom-switch"
									label={translate('PlaceHolder.currentlyWorking')}
									className="switch-btn position-absolute top-0 end-0"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									checked={formik.values.currentlyWorking}
									autoComplete="off"
									name="currentlyWorking"
								/>
								{renderError('currentlyWorking')}
								<DatePicker
									selected={formik.values.endDate}
									onChange={(date: any) => formik.setFieldValue('endDate', date)}
									name="endDate"
									onBlur={formik.handleBlur}
									dateFormat="MM/yyyy"
									showMonthYearPicker
									disabled={formik.values.currentlyWorking}
									placeholderText={`${translate('PlaceHolder.date')}`}
									maxDate={new Date()}
									autoComplete="off"
								/>
								{renderError('endDate')}
							</Form.Group>
						</Col>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('Common.rolesAndResponsibility')}
								</Form.Label>
								<Form.Control
									as="textarea"
									rows={4}
									placeholder={translate('PlaceHolder.writeHere')}
									name="rolesAndResponsibility"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.rolesAndResponsibility}
									autoComplete="off"
								/>
								{renderError('rolesAndResponsibility')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						<Button className="filled-btn rounded-pill" type="submit">
							{translate('Common.save')}
						</Button>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default AddExperience;
